import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'

/**
 * Whether a chain member's **text content** may be rewritten — the shared gate for every chain
 * operation that writes `paragraphs` and nothing else.
 *
 * **`locked` blocks; `frozen` does not (WMPCM-6980, following WMPCM-6972).** The two states are not
 * interchangeable: `locked` rejects every edit, while `frozen` rejects only placement geometry —
 * `x/y/width/height/rotation`, the `PLACEMENT_GEOMETRY_KEYS` opt-in block list in `lib/groups/`.
 * `shouldBlockShapeUpdate` already encodes exactly that, and lets a paragraph-only update through
 * on a frozen frame. A chain operation that moves or restyles text therefore has no business
 * consulting `frozen`, any more than `canLink` does (see its warning block in
 * `text-chain-mutator.ts`).
 *
 * `frozen` is named in the parameter type rather than ignored implicitly so that a future reader
 * sees the omission is a decision, not an oversight — folding it in beside `locked` is the exact
 * mistake this predicate was extracted to stop. Both defects it replaced were spelled
 * `f.locked || f.frozen` and both lived in `pages/home`, which the test suite never renders; the
 * behaviour is pinned by this module's co-located test instead.
 */
export function canEditChainFrameContent(
  frame: Pick<LinkableTextFrame, 'locked' | 'frozen'>
): boolean {
  return frame.locked !== true
}

/**
 * Shown when a destructive edit — cut, Delete/Backspace, or typing over the selection — spans a
 * cross-frame text selection that includes a locked chain member (WMPCM-9728).
 *
 * The edit is refused whole rather than applied to the unlocked members only, because a partial
 * delete is exactly the case that lets the follow-on reflow pull content backward
 * (now unconditional, D4): emptying the neighbours would reclaim the locked frame's
 * paragraphs into the frame upstream of it, relocating the text instead of protecting it.
 *
 * Worded to match `LINKED_TEXT_GROUP_BLOCKED_MESSAGE` and
 * `LINKED_TEXT_PHOTO_DROP_BLOCKED_MESSAGE` — name the offending object and the one action that
 * unblocks it. A restyle is not refused (it changes no lengths, so nothing reflows) and needs no
 * message: it skips the locked member exactly as the whole-frame style path in `pages/home` does.
 */
export const CHAIN_LOCKED_MEMBER_EDIT_BLOCKED_MESSAGE =
  'Cannot edit text across a locked linked frame. Unlock it first.'

/**
 * Shown when a linking session's click lands on a locked text frame (WMPCM-10578 #3). `canLink`
 * refuses a locked target (`'target-locked'`), and the click used to fall through to the
 * empty-canvas branch instead, drag-creating a NEW linked frame right on top of the locked one —
 * which read as though the locked frame had accepted the link. Same wording pattern as
 * `CHAIN_LOCKED_MEMBER_EDIT_BLOCKED_MESSAGE`.
 */
export const LOCKED_LINK_TARGET_MESSAGE = 'Cannot link to a locked text frame. Unlock it first.'
